import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { PageInterface } from '../../planner/page/page.interface';
import { ShiftRecord } from 'src/shared/models/shift-record.model';
import { AcuityShiftRecordService } from 'src/shared/services/shift-record-acuity.service';
import { ShiftRecordStatus } from 'src/shared/models/shift-record-status';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';
import { WorkflowPage } from 'src/shared/models/workflow-page.model';
import { AncillaryConfiguration } from 'src/shared/models/ancillary-configuration.model';
import { RoomAcuityRecord } from 'src/shared/models/room-acuity-record.model';
import { Room } from 'src/shared/models/room.model';
import { AcuityRoomsUtility } from '../acuity-rooms-utility.service';

import * as moment from 'moment';
import { Router } from '@angular/router';
import { WorkflowPageComponent } from '../../base-classes/workflow-page/workflow-page.component';
import { AcuityCategory } from 'src/shared/models/acuity-category.model';

@Component({
  selector: 'smt-review-previous-rooms',
  templateUrl: './review-previous-rooms.component.html',
  styleUrls: ['./review-previous-rooms.component.scss']
})
export class ReviewPreviousRoomsComponent extends WorkflowPageComponent implements OnInit, PageInterface {
    @Input() page: WorkflowPage;
    @Output() previousRequested = new EventEmitter();

    public loading: boolean;
    public prevShiftRecord: ShiftRecord;
    public previousBlockForm:UntypedFormGroup = new UntypedFormGroup({}); 
    public previousBlockFormValues$;
    public shiftRecord: ShiftRecord;
    public isEditing: boolean;
    public formGroups: UntypedFormGroup[];
    private acuityCount = new Map<string, number>();
    public acuityCategories: AcuityCategory[];

    get ancillaryConfiguration(): AncillaryConfiguration {
        return this.shiftRecord.costCenter.ancillaryConfiguration;
    }

    get hasRoomAcuityRecords(): boolean {
        return this.prevShiftRecord && this.prevShiftRecord.roomAcuityRecords && this.prevShiftRecord.roomAcuityRecords.length > 0;
    }

    constructor(private _router: Router,
        private _acuityShiftRecordService: AcuityShiftRecordService, 
        private _utility: WorkflowUtilityService,
        private _acuityRoomsUtility: AcuityRoomsUtility) {
            super(_router);
    }

    async ngOnInit() {
        super.initialize(this.page, () => this.initialize());
    }

    initialize() {        
      this.prevShiftRecord = this.shiftRecord.prevShiftRecord;

        // Editing is only allowed when there is previous shift record data
        if (this.prevShiftRecord && this.prevShiftRecord.roomAcuityRecords) {        
            this.prevShiftRecord.costCenter.ancillaryConfiguration.roomTypes.forEach(roomType => {
                roomType.rooms.forEach(room => {
                    this.previousBlockForm.addControl('room' + room.id, new UntypedFormControl({value: ''}, Validators.required));
                });
            });

            this.previousBlockForm.addControl('reviewed', new UntypedFormControl(false, Validators.requiredTrue));            
    
            this.previousBlockFormValues$ = this.previousBlockForm.valueChanges.subscribe(values => {                
                this.acuityCount = this._acuityRoomsUtility.updateAcuityTotal(values, this.ancillaryConfiguration);
            });

            this.acuityCount = this._acuityRoomsUtility.populateRoomAcuityValues(this.prevShiftRecord.costCenter.ancillaryConfiguration, this.prevShiftRecord, this.previousBlockForm);            
        }

        this.formGroups = [ this.previousBlockForm ];          
        if (this.prevShiftRecord) {
            this.acuityCategories = this._utility.buildAcuityCategories(this.prevShiftRecord);
        }        
    }

    isFormValid(): boolean {
        return (this.previousBlockForm && this.previousBlockForm.valid);
    }

    formatShiftBlockTime() {
        return this._utility.formatShiftTime(this.prevShiftRecord.shiftBlock, this.prevShiftRecord.workflowTimeFacility);
    }

    toggleIsEditing() {
        this.isEditing = !this.isEditing;
        if (this.isEditing) {
            let control = this.previousBlockForm.get('reviewed');
            control.setValue(false);
            control.markAsUntouched();
            control.markAsPristine();
        }
    }

    getRoomAcuity(room: Room) : RoomAcuityRecord {
        return this._acuityRoomsUtility.getRoomAcuity(this.prevShiftRecord, room);
    }

    save(): Promise<any> {
        this.setStatus();
        if (!this.prevShiftRecord) {
            return this._acuityShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);
        }
        
        if (this.hasRoomAcuityRecords) {
            this._acuityRoomsUtility.setRoomAcuities(this.ancillaryConfiguration, this.prevShiftRecord, this.previousBlockForm);
            this.prevShiftRecord.acuityCensusRecords = this._acuityRoomsUtility.createAcuityCensusRecords(this.acuityCount, this.shiftRecord);
        }
        
        return this._acuityShiftRecordService.setShiftRecordWithAssociated(this.prevShiftRecord).then((prevShiftRecordId) => {
            this.shiftRecord.prevShiftRecordId = prevShiftRecordId;
            return this._acuityShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);
        }, 
        (errors) => { console.error(errors); return 0; });
    }

    shouldSkipSave(): boolean {
        // Find the reviewed toggle from the form.
        let control = this.previousBlockForm.get('reviewed');

        if (control) {
            // Ignore the reviewed toggle when determining if the form is dirty.
            control.markAsUntouched();
            control.markAsPristine();
        }        

        // Only actually save if the form is dirty once the reviewed toggle is ignored.
        return this.isFormValid() && !this.previousBlockForm.dirty;
    }
    
    private setStatus() {
        if (this.shiftRecord.status !== ShiftRecordStatus.Done) {
            this.shiftRecord.status = ShiftRecordStatus.EnterCurrent;
        }
    }
    
    private isPreviousShiftBeforeBlockActivated(): boolean {
        if (!this.shiftRecord.prevShiftRecord) {
            return true;
        }
        var facilityWorkflowTime = new Date(this.shiftRecord.prevShiftRecord.workflowTimeFacility);
        var shiftBlockCreated = moment.utc(this.shiftRecord.prevShiftRecord.shiftBlock.created).local().toDate();
        return this.shiftRecord.prevShiftRecord &&  facilityWorkflowTime < shiftBlockCreated;
    }
}
